<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <top-bar v-if="showTopBar" :next="backToChallenge">
      <span slot="challenge">
        <!--
          Never use v-html with end-user generated content, because it's a vector for XSS attacks
          This is what the following line used to be:
          {{ challengeTitle }}
        -->
        <div v-html="topBarText"></div>
      </span>
      <cash-navbar slot="cash"></cash-navbar>
      <metrics-navbar slot="metrics"></metrics-navbar>
    </top-bar>
  </transition>

  <transition
    :enter-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationEnter)) ? '' : 'animated slideInLeft'"
    :leave-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationLeave)) ? '' : 'animated fadeOut'"
  >
    <player
      v-if="showPlayer"
      :angled="notification != null"
      key="player"
      :variantOverride="playerVariant"
    >
    </player>
  </transition>

  <transition
    :enter-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationEnter)) ? '' : 'animated slideInRight'"
    :leave-active-class="(teamDecision && (teamDecision.noAnimation || teamDecision.noAnimationLeave)) ? '' : 'animated slideOutRight'"
  >
    <narrator
      v-if="showNarrator && !showNarrative"
      :character="narratorCharacter"
      :variant-override="narratorVariant"
    >
    </narrator>
  </transition>

  <bottom-bar v-if="!showNarrative"
    :next="(showButton || waiting) ? onNext : undefined"
    :show-player="showPlayerName"
    :tabindex="1"
    :disable-next="waiting"
  >
    <span slot="button">
      <span v-if="nextNarratorText" id="decide-btn">{{(game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next'}}</span>
      <span v-else-if="currentState === resultsState" id="decide-btn">{{this.endNavigationButtonText}}</span>
      <span v-else-if="waiting" id="decide-btn">Waiting...</span> <!-- TODO: make waiting more informative? -->
      <span v-else-if="playerDecision == null" id="decide-btn">Continue</span> <!-- TODO: make decisionless state more informative? -->
      <span v-else id="decide-btn">Make a decision</span>
    </span>
  </bottom-bar>

  <transition enter-active-class="animated fadeInRight" leave-active-class="animated fadeOutRight">
    <team v-if="showTeam" id="team" :animate-out="animateOutTeam" :show-last="currentState === resultsState"></team>
    <notification-panel
      v-else-if="showGroupNotificationPanel && !(showNarrative || nextNarratorText || (currentState === resultsState)) && (waiting || playerDecision == null)"
      class="notification-panel"
      :class="{ sf: starfighter }"
    >
    </notification-panel>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <narrator-notification
      v-if="showNotification && !showNarrative"
      id="narrator-notification"
      width="405px"
      :style="notificationStyle">
      <!--
        Never use v-html with end-user generated content, because it's a vector for XSS attacks,
        but this is okay here since narratorText is only used/set internally.
        AFAIK, this is the simplest way to support hyperlinks in dialogue, and it also enables any html formatting.
        This is what the following line used to be:
        {{ notification }}
      -->
      <p v-html="notification"></p>
    </narrator-notification>
  </transition>

  <narrative-player v-if="showNarrative" :narratives="narrative" :on-complete="onNarrativePlayerComplete">
  </narrative-player>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import BottomBar from '../ui/BottomBar';
import TopBar from '../ui/TopBar';
import Player from '../ui/Player';
import CashNavbar from '../ui/CashNavbar';
import MetricsNavbar from '../ui/MetricsNavbar';
import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import Team from '../ui/Team';
import NarrativePlayer from '../narrative/NarrativePlayer';
import NotificationPanel from '../ui/NotificationPanel';

export default {
  components: {
    BottomBar,
    TopBar,
    Player,
    CashNavbar,
    MetricsNavbar,
    Narrator,
    NarratorNotification,
    Team,
    NarrativePlayer,
    NotificationPanel
  },
  data() {
    return {
      animateOutTeam: false,
      initialWaiting: false,
      showNarrator: false,
      showNotification: false,
      showGroupNotificationPanel: false,
      showButton: false,
      showTeam: false,
      showPlayer: false,
      showPlayerName: false,
      currentGroupTeamDecision: {},
      updating: false,
      narratorTextCounter: 0,
      showNarrative: false,
      waiting: false,
      intervalID: null,
      failCount: 0,
      handleOutroOnProgress: false,
    };
  },
  computed: {
    ...mapGetters([
      'backToDashboard',
      'dashboardProcessingTickDelay',
      'isWaiting',
      'playerDecision',
      'challenge',
      'groupTeamDecision',
      'teamDecision',
      'lastTeamDecision',
      'complete',
      'proxyRole',
      'currentState',
      'dashboardState',
      'entryState',
      'gameIntroState',
      'avatarSelectState',
      'backstorySelectState',
      'challengeIntroState',
      'challengeOutroState',
      'resultsState',
      'teamDecisionUpdated',
      'lastChallenge',
      'lastChallengeOutroViewed',
      'group',
      'playerGroupRole',
      'updateGroup',
      'playerLastDecision',
      'lastChoice',
      'narrativeIndex',
      'game',
      'skipOutrosEnabled',
      'characters',
      'native',
      'starfighter'
    ]),
    challengeTitle() {
      if (this.lastChallengeOutroViewed) {
        return this.challenge.title;
      }
      return this.lastChallenge.title;
    },
    topBarText() {
      let t = (this.teamDecision || {}).topBarText;
      if (this.currentState === this.resultsState) {
        t = (this.lastTeamDecision || {}).topBarText;
      }
      if (t == null || t === '') {
        t = this.challengeTitle;
      }
      return t;
    },
    narratorCharacter() {
      if (this.currentState === this.resultsState) {
        const id = (this.lastTeamDecision || {}).rightCharacter;
        if (id) {
          return this.characters[id];
        }
      }
      const id = (this.teamDecision || {}).rightCharacter;
      if (id) {
        return this.characters[id];
      }
      return undefined;
    },
    narratorVariant() {
      if (this.currentState === this.resultsState) {
        return (this.lastTeamDecision || {}).rightVariant;
      }
      return (this.teamDecision || {}).rightVariant;
    },
    playerVariant() {
      if (this.currentState === this.resultsState) {
        return (this.lastTeamDecision || {}).playerVariant;
      }
      return (this.teamDecision || {}).playerVariant;
    },
    notificationYOffset() {
      if (this.narratorCharacter && this.narratorVariant && this.narratorCharacter.imageVariants) {
        const variant =
          this.narratorCharacter.imageVariants.find(v => v.name === this.narratorVariant);
        return ((variant && variant.speechYOffset) ? variant.speechYOffset : '0px');
      }
    },
    notificationStyle() {
      return {
        top: ((this.notificationYOffset) ? `calc(428px - ${this.notificationYOffset}` : '428px')
      };
    },
    narratorText() {
      if (this.currentState === this.resultsState) {
        return (this.playerLastDecision || {}).narratorOutros || [];
      }
      return (this.playerDecision || {}).narratorIntros || [];
    },
    currentNarratorText() {
      return this.narratorText[this.narratorTextCounter];
    },
    nextNarratorText() {
      return this.narratorText[this.narratorTextCounter + 1];
    },
    notification() {
      // Multiplayer
      if (this.proxyRole) {
        return `Make a decision for the ${this.proxyRole.title}`;
      }

      // Designer-specified narrator text
      if (this.currentNarratorText) {
        return this.currentNarratorText.text;
      }

      return null;
    },
    narrative() {
      if (this.currentState === this.resultsState) {
        const lastChoice = ((this.lastChoice && this.lastChoice.wrong) ? {} : this.lastChoice);
        const choiceNarratives = (lastChoice || {}).narratives || [];
        // console.log(lastChoice);
        if (this.skipOutrosEnabled) {
          choiceNarratives.length = 0; // clear narratives if skipping outros
        } else if (lastChoice && Array.isArray(lastChoice) && choiceNarratives.length === 0) {
          // add selected narratives

          lastChoice.forEach((choice) => {
            if (choice.selectedNarratives && choice.selectedNarratives.length) {
              choiceNarratives.push(...(choice.selectedNarratives));
            }
          });

          // add unselected narratives

          const allLastChoices = (this.playerLastDecision || {}).choices || [];
          const unselectedCategory = (this.playerLastDecision || {}).unselectedCategory || null;
          // console.log(allLastChoices);
          allLastChoices.forEach((choice) => {
            // choice._id is weirdly not always unique, so technically can't rely on just that field
            if (
              !(lastChoice.some(
                choiceMade =>
                  choiceMade.title === choice.title && choiceMade._id === choice._id
                  && (!(choiceMade.category)
                    || (`${choiceMade.category._id}` === `${choice.correctCategory}`
                      && `${choiceMade.category._id}` !== `${unselectedCategory}`)
                  )
              ))
              && choice.unselectedNarratives && choice.unselectedNarratives.length
            ) {
              // unselected and has unselected narrative
              choiceNarratives.push(...(choice.unselectedNarratives));
            }
          });
        }
        return [
          ...choiceNarratives,
          ...((this.playerLastDecision || {}).outroNarratives || []),
          ...((this.lastTeamDecision || {}).outroNarratives || [])
        ];
      }
      return [
        ...((this.teamDecision || {}).introNarratives || []),
        ...((this.playerDecision || {}).introNarratives || [])
      ];
    },
    mainOutroStartIndex() {
      const lastChoice = ((this.lastChoice && this.lastChoice.wrong) ? {} : this.lastChoice);
      return ((lastChoice || {}).narratives || []).length;
    },
    showTopBar() {
      return ((this.lastTeamDecision && (this.currentState === this.resultsState))
        ? ((this.narrativeIndex < this.mainOutroStartIndex)
          ? !(this.lastTeamDecision.hideDashInChoiceNarratives)
          : !(this.lastTeamDecision.hideDashInOutroNarratives))
        : !(this.teamDecision.hideDashInNarratives));
    },
    endNavigationButtonText() {
      const skip =
        this.lastTeamDecision != null
          ? this.lastTeamDecision.skipResults
          : (this.teamDecision != null ? this.teamDecision.skipResults : false);
      return skip ? `${this.game.nextButtonDefaultText || 'Next'}` : 'See Results';
    }
  },
  methods: {
    ...mapMutations([
      'setBackToDashboard',
      'setDashboardProcessingTickDelay',
      'setIsWaiting',
      'refreshActiveView'
    ]),
    ...mapActions([
      'stopSounds',
      'setLastStateBeforeChallengeIntro',
      'resetProxyRole',
      'getMyGroup',
      'pollGroup',
      'submitDecisionless'
    ]),
    backToChallenge() {
      const lastStateBeforeChallengeIntro = this.dashboardState;
      this.setLastStateBeforeChallengeIntro({ lastStateBeforeChallengeIntro });
      this.$router.push(this.challengeIntroState);
    },
    async onNext() {
      // "Continue" (decisionless skip)
      // part of this check might be redundant but erring on the side of caution
      if (!(this.nextNarratorText || (this.currentState === this.resultsState) || this.waiting)
        && this.playerDecision == null) {
        try {
          await this.submitDecisionless();
        } catch (err) {
          await this.poll();
        }
        await Vue.nextTick();
      }

      if (this.nextNarratorText) {
        this.advanceNarrative();
      } else if (this.currentState === this.resultsState
        && this.narrative.length > 0) {
        this.hideAll();
        this.showNarrative = true;
      } else {
        this.navigateWithAnimation();
      }
    },
    async navigateWithAnimation() {
      if (this.updating) return;

      this.showButton = false;
      if (this.showNotification) {
        this.showNotification = false;
        await new Promise(resolve => setTimeout(resolve, 750));
        if (!(this.teamDecision && (this.teamDecision.noAnimation || this.teamDecision.noAnimationLeave))) {
          this.showNarrator = false;
          this.showPlayer = false;
          if (!this.showTeam) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      if (this.showTeam) {
        this.animateOutTeam = true;
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      this.$router.push(this.currentState);
    },
    async animateInNarrator() {
      if (this.notification != null) {
        this.showNarrator = true;
        if (!(this.teamDecision && (this.teamDecision.noAnimation || this.teamDecision.noAnimationEnter))) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        this.showNotification = true;
        await new Promise(resolve => setTimeout(resolve, 1250));
      } else if (!this.showTeam && !this.waiting && !this.dashboardProcessingTickDelay) {
        this.onNext();
      }
      this.showButton = true;
    },
    async advanceNarrative() {
      this.showNotification = false;
      this.showButton = false;
      this.narratorTextCounter += 1;
      if (this.notification != null) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.showNotification = true;
        await new Promise(resolve => setTimeout(resolve, 1250));
      }
      this.showButton = true;
    },
    async setUpNarratorText() {
      const showTeam =
        (this.lastTeamDecision && (this.currentState === this.resultsState))
          ? !(this.lastTeamDecision.hideTeam)
          : !(this.teamDecision.hideTeam);

      this.showPlayer = true;

      if (this.notification != null
        && (this.teamDecision && (this.teamDecision.noAnimation || this.teamDecision.noAnimationEnter))) {
        this.showNarrator = true;
      }

      if (!showTeam && this.notification != null
        && !(this.teamDecision && (this.teamDecision.noAnimation || this.teamDecision.noAnimationEnter))) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      this.showPlayerName = true;
      if (!showTeam && this.notification != null) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      this.showTeam = showTeam;

      this.showGroupNotificationPanel = true;

      if (this.currentState !== this.dashboardState) {
        if (!showTeam && this.notification != null) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        this.animateInNarrator();
      } else if (this.narrative == null || this.narrative.length === 0
        || this.playerDecision == null) {
        this.showButton = true;
      }
    },
    hideAll() {
      this.showNarrator = false;
      this.showNotification = false;
      this.showGroupNotificationPanel = false;
      this.showPlayer = false;
      this.showPlayerName = false;
      this.showNarrative = false;
      this.showTeam = false;
      this.showButton = false;
    },
    async onNarrativePlayerComplete() {
      if (this.currentState === this.resultsState) { // outro
        this.navigateWithAnimation();
      } else {
        this.hideAll();
        await this.setUpNarratorText();
      }
    },
    async poll() {
      try {
        await this.pollGroup();
        this.failCount = 0;
        // if all decisions with lower order have choices registered, then unset waiting.
        const allDependenciesAnswered =
          this.teamDecision.decisions
            .filter(({ order }) => this.playerDecision == null || this.playerDecision.order > order)
            .every(({ _id }) => !!((this.groupTeamDecision.decisions.find(d => `${d._id}` === `${_id}`)).completed));
        if (allDependenciesAnswered) {
          this.waiting = false;
          window.clearInterval(this.intervalID);
          this.intervalID = null;
        }
      } catch (err) {
        console.error(err);
        this.failCount += 1;
        if (this.failCount > 3) {
          if (await this.$router.handleGameError(err)) return;
          this.$router.showGameErrorCritical();
        }
      }
    },
    setUpPoll() {
      // if not polling and if starting a teamDecision and if the group is multiplayer
      // and EITHER there's no playerDecision and the Wait If No Decision flag is set
      // OR playerDecision.order > some other teamDecision.decisions.order,
      // then set waiting and poll until all those decisions have choices registered,
      // then unset waiting.
      if (!(this.waiting) && this.currentState !== this.resultsState
        && this.group.players.length > 1
        && ((this.playerDecision == null && this.teamDecision.waitIfNoDecision)
          || (this.playerDecision != null && this.playerDecision.order > 0))) {
        const allDependenciesAnswered =
          this.teamDecision.decisions
            .filter(({ order }) => this.playerDecision == null || this.playerDecision.order > order)
            .every(({ _id }) => !!((this.groupTeamDecision.decisions.find(d => `${d._id}` === `${_id}`)).completed));
        if (allDependenciesAnswered) {
          this.waiting = false;
        } else {
          this.waiting = true;
          this.intervalID = window.setInterval(this.poll, 10000);
        }
      } else {
        this.waiting = false;
      }
    }
  },
  watch: {
    // proxy player
    async playerDecision(newValue) {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (this.proxyRole && newValue) {
        this.animateInNarrator();
      }
    },
    group(newValue, oldValue) {
      if (newValue && (!oldValue || oldValue.updated !== newValue.updated)) {
        const allDependenciesAnswered =
          this.teamDecision.decisions
            .filter(({ order }) => this.playerDecision == null || this.playerDecision.order > order)
            .every(({ _id }) => !!((this.groupTeamDecision.decisions.find(d => `${d._id}` === `${_id}`)).completed));
        if (allDependenciesAnswered) {
          this.waiting = false;
          if (this.intervalID) {
            window.clearInterval(this.intervalID);
            this.intervalID = null;
          }
        }
      }
    }
  },
  async created() {
    const roleId = this.playerGroupRole._id;
    this.resetProxyRole();
    await Vue.nextTick();

    const roleHasChanged = this.playerGroupRole._id !== roleId;
    if (roleHasChanged && this.entryState !== this.dashboardState
      || [
        this.gameIntroState,
        this.avatarSelectState,
        this.backstorySelectState,
        this.challengeOutroState,
      ].includes(this.currentState)) {
      this.$router.nextGameState();
    }
  },
  async mounted() {
    this.hideAll();
    await Vue.nextTick();
    this.stopSounds();

    this.setUpPoll();

    if (this.currentState === this.resultsState) { // outro
      await this.setUpNarratorText();
    } else if (this.backToDashboard) {
      this.setBackToDashboard({ backToDashboard: false });
      if (this.narrative.length > 0) { // introNarrative
        this.showNarrative = true;
      } else { // narrator intro
        // TODO: add support for going back to the last Narrator Text here; for now, not supported
        // (if you have a narrator intro and an introNarrative, going back will go to the last narrative)
        console.error('impossible state: backtracking without an intro narrative should not currently be possible.');
        await this.setUpNarratorText();
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (this.narrative.length > 0) { // introNarrative
        this.showNarrative = true;
      } else { // narrator intro
        await this.setUpNarratorText();
      }
    }
  },
  beforeDestroy() {
    if (this.intervalID != null) {
      window.clearInterval(this.intervalID);
    }
  }
};
</script>

<style lang="scss" scoped>
#decide-btn {
  font-size: 32px;
  line-height: 2.6em;
  justify-content: center;
  vertical-align: middle;
}
#narrator-notification {
  right: 18px;
}
#team {
  position: absolute;
  top: 130px;
  left: 380px;
}
.notification-panel {
  position: absolute;
  top: 125px;
  left: 500px;
}
.notification-panel.sf {
  top: 130px;
  left: 380px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/pages/Dashboard.vue